import { type ReactNode } from 'react'
import NextImage, { type ImageProps } from 'next/image'
import Link from 'next/link'
import clsx from 'clsx'
import { makeAbsolute } from '@/utils/url'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { clampedText, contentWrapper, extendedLink, roundedImage } from './ArticleTeaser.css'

type Props = {
  children: ReactNode
  title: string
  ingress: string
  href: string
  date: string
}

const Root = (props: Props) => {
  return (
    <div className={yStack({ position: 'relative', gap: 'md' })}>
      <div className={yStack({ gap: 'md' })}>
        {props.children}
        <div className={contentWrapper}>
          <Text size="sm" color="textSecondary" uppercase={true}>
            {props.date}
          </Text>
        </div>
      </div>
      <div className={contentWrapper}>
        <div className={clampedText}>
          <Link className={extendedLink} href={makeAbsolute(props.href)} style={{ display: 'block' }}>
            <Heading as="h3" variant="standard.20">
              {props.title}
            </Heading>
          </Link>
          {/* We can't use flex to space heading and paragraph texts while making them clamped */}
          <Text className={sprinkles({ mt: 'xxs' })} as="p" size="lg" color="textSecondary">
            {props.ingress}
          </Text>
        </div>
      </div>
    </div>
  )
}

type ArticleTeaserImageProps = Omit<ImageProps, 'priority'> & { imagePriority?: boolean }

const Image = ({ className, imagePriority, ...forwardedProps }: ArticleTeaserImageProps) => (
  <NextImage
    className={clsx(roundedImage, className)}
    loading={imagePriority ? 'eager' : forwardedProps.loading}
    fetchPriority={imagePriority ? 'high' : forwardedProps.fetchPriority}
    {...forwardedProps}
  />
)

export const ArticleTeaser = {
  Root,
  Image,
} as const
